import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { GameCard } from '../features/product/types';
import ResponsiveImage from './ResponsiveImage';

const priceFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

const getFeatures = (description?: string) => {
  const features = (description ?? '')
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-•]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 5);

  return features.length > 0 ? features : ['Моментальная активация', 'Поддержка после покупки'];
};

const SUBSCRIPTION_LEVELS = ['Essential', 'Extra', 'Deluxe'] as const;

const getLevelProducts = (products: GameCard[], duration: number) =>
  SUBSCRIPTION_LEVELS.flatMap(level => {
    const normalizedLevel = level.toLocaleLowerCase('ru-RU');
    const product = products.find(item =>
      (item.plans ?? []).some(plan => plan.tier?.trim().toLocaleLowerCase('ru-RU') === normalizedLevel)
    );
    if (!product) return [];

    const tierPlans = (product.plans ?? []).filter(
      plan => plan.tier?.trim().toLocaleLowerCase('ru-RU') === normalizedLevel
    );
    const selectedPlan = tierPlans.find(plan => plan.durationMonths === duration) ?? tierPlans[0];
    return selectedPlan ? [{ level, product, selectedPlan }] : [];
  });

const SubscriptionCarousel = ({ products }: { products: GameCard[] }) => {
  const durations = [1, 3, 12];
  const [duration, setDuration] = useState(1);
  const levelProducts = getLevelProducts(products, duration);

  if (levelProducts.length === 0) return null;

  return (
    <div>
      <div className="mb-5 flex flex-col justify-between gap-5 sm:mb-10 lg:flex-row lg:items-center">
        <h2 className="text-2xl font-medium leading-none text-foreground sm:text-[32px] md:text-[38px]">Подписки PS Plus</h2>
        <div className="flex min-h-[50px] w-full min-w-0 sm:w-auto sm:self-start rounded-xl border border-foreground/10 bg-card p-1.5 lg:self-auto">
          {durations.map(months => (
            <button
              key={months}
              type="button"
              onClick={() => setDuration(months)}
              className={`flex min-w-0 flex-1 items-center justify-center whitespace-nowrap rounded-lg px-2 py-2 text-xs min-[375px]:text-sm sm:px-4 font-bold transition-all ${duration === months ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-md shadow-red-900/20' : 'text-foreground/45 hover:bg-muted hover:text-foreground'}`}
            >
              {months} {months === 1 ? 'месяц' : months < 5 ? 'месяца' : 'месяцев'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-3 md:gap-6">
        {levelProducts.map(({ level, product, selectedPlan }) => {
          const displayPrice = selectedPlan.price;
          return (
            <Link
              key={`${product.id}-${level}`}
              to={`/service/${product.id}`}
              className="group flex h-full w-full min-w-0 gap-3 rounded-xl border border-foreground/10 bg-card p-2 text-card-foreground shadow-sm transition-colors duration-300 hover:border-red-500/35 sm:gap-5 sm:px-6 sm:py-6 md:flex-col md:items-stretch md:gap-5 md:p-6"
            >
              <div className="relative w-24 aspect-[3/2] min-[375px]:w-28 shrink-0 overflow-hidden rounded-md bg-muted shadow-xl sm:w-[40%] sm:aspect-[21/10] md:w-full">
                <ResponsiveImage
                  src={selectedPlan.picture || product.picture}
                  sources={selectedPlan.pictureSources || product.pictureSources}
                  alt={product.name}
                  sizes="(min-width: 1280px) 320px, (min-width: 768px) 45vw, 80vw"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              </div>

              <div className="flex min-w-0 flex-1 items-center md:block">
                <div className="flex w-full min-w-0 flex-col gap-1 xl:flex-row xl:flex-wrap xl:items-start xl:justify-between xl:gap-2">
                  <h3 className="line-clamp-2 text-base font-medium sm:text-xl">{level}</h3>
                  <span className="shrink-0 whitespace-nowrap text-lg font-semibold sm:text-xl">
                    {priceFormatter.format(displayPrice)}
                  </span>
                </div>

                <ul className="mt-4 hidden space-y-1.5 md:block">
                  {getFeatures(selectedPlan.description || product.description).map((feature, index) => (
                    <li key={`${feature}-${index}`} className="flex items-start gap-2 text-sm text-foreground/50">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-red-500">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      <span className="line-clamp-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionCarousel;
